/**
 * EL TEXTO QUE VE UNA PERSONA CUANDO ALGO FALLA — H68 (auditoría 16-sep-2026).
 *
 * Por toda la app se repetía `Alert.alert('Error', e?.message ?? 'texto amable')`.
 * El `??` solo entra cuando NO hay mensaje, y Firebase y las Cloud Functions
 * casi siempre traen uno: en inglés y técnico. Quien tecleaba mal el código SMS
 * leía "Firebase: Error (auth/invalid-verification-code)." y quien pagaba un
 * pedido con el servidor caído leía "INTERNAL". Para un usuario —y para quien
 * revisa la app en Google Play— eso es una app rota.
 *
 * La clasificación es la misma que ya hacía bien `map.tsx` al pedir un viaje:
 *   · un mensaje que escribimos NOSOTROS en una Cloud Function o un servicio (en
 *     español, para una persona) → se respeta tal cual, y va PRIMERO: gana a
 *     cualquier código
 *   · sin permiso      → cuenta sin verificar o sesión vieja
 *   · sin sesión       → que vuelva a entrar
 *   · sin red          → que revise la conexión
 *   · demasiados intentos → que espere
 *   · códigos SMS      → dicho en claro
 *   · todo lo demás    → el texto amable que cada pantalla ya tenía escrito
 *
 * Solo decide el TEXTO. No reintenta, no navega y no cambia ninguna lógica.
 */

const SIN_RED = ['unavailable', 'network-request-failed', 'deadline-exceeded', 'network request failed', 'timeout', 'timed out'];

/** ¿Este texto lo escribió una persona para otra, o es jerga de la librería? */
function pareceMensajeHumano(texto: string): boolean {
  const t = texto.trim();
  if (t.length < 12) return false;
  // Jerga técnica o mayúsculas de código: INTERNAL, NOT_FOUND, "Firebase: …".
  if (/firebase|firestore|internal|exception|insufficient permissions|\bundefined\b|\bnull\b|^[A-Z_]+$|\[[a-z-]+\/[a-z-]+\]|auth\//i.test(t)) {
    return false;
  }
  // Inglés o detalle de una API ("Places API error: 500", "Your card was
  // declined", "No document to update"): no es un texto para la persona.
  if (/\b(the|you|your|is|are|was|of|to|and|failed|cannot|could|must|missing|invalid|unknown|request|document|http|api)\b/i.test(t)) {
    return false;
  }
  // Una frase en español: letras con tilde/ñ o palabras comunes del idioma.
  return /[áéíóúñ¿¡]|\b(tu|tus|el|la|los|las|no|ya|de|para|con|por|que|un|una)\b/i.test(t);
}

export function mensajeDeError(e: any, textoAmable: string): string {
  const codigo = String(e?.code ?? '').toLowerCase();
  const mensaje = String(e?.message ?? '');
  const todo = `${codigo} ${mensaje.toLowerCase()}`;

  // V16/V26/V44/V45 — El motivo escrito para la persona gana a la clasificación
  // por código. Antes un permission-denied o un internal con "Tienes una multa
  // por cancelaciones pendiente…", "PIN incorrecto…" o "Borramos tus datos,
  // pero…" se cambiaba por un texto genérico que no decía cómo salir del paso.
  // Lo técnico ("Missing or insufficient permissions", "INTERNAL") no pasa el
  // filtro y sigue cayendo en las ramas de abajo.
  if (pareceMensajeHumano(mensaje)) {
    return mensaje.trim();
  }
  if (todo.includes('permission-denied') || todo.includes('insufficient permissions')) {
    return 'Tu cuenta no tiene permiso para esto todavía. Si acabas de registrarte, termina la verificación desde tu perfil; si no, cierra sesión y vuelve a entrar.';
  }
  if (todo.includes('unauthenticated') || todo.includes('user-token-expired') || todo.includes('requires-recent-login')) {
    return 'Tu sesión se cerró. Vuelve a entrar para continuar.';
  }
  if (todo.includes('invalid-verification-code')) {
    return 'El código no es correcto. Revísalo e intenta de nuevo.';
  }
  if (todo.includes('code-expired') || todo.includes('session-expired')) {
    return 'El código venció. Pide uno nuevo e intenta otra vez.';
  }
  if (todo.includes('too-many-requests') || todo.includes('resource-exhausted') || todo.includes('quota-exceeded')) {
    return 'Hiciste demasiados intentos seguidos. Espera un momento y vuelve a intentar.';
  }
  if (SIN_RED.some((s) => todo.includes(s))) {
    return 'No pudimos conectar con el servidor. Revisa tu internet e intenta de nuevo.';
  }
  return textoAmable;
}
